/* eslint-disable no-unused-vars */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Box, Button, Flex, Text, useDisclosure } from '@chakra-ui/react';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate, useParams } from 'react-router-dom';
import _ from 'lodash';
import { useFormValidationToast } from '../../../services/actionToast';
import { InputForm } from '../../inputs/forms/InputForm';
import { FormButton } from '../../buttons/FormButton';
import { ReturnButton } from '../../buttons/ReturnButton';
import { useFetch } from '../../../hooks/useFetch';
import { SelectInputForm } from '../../inputs/forms/SelectInputForm';
// import { PasswordInputForm } from '../../inputs/forms/PasswordInputForm';
import { ImageInputForm } from '../../inputs/forms/ImageInputForm';
import { SwitchInputForm } from '../../inputs/forms/SwitchInputForm';
import AlertModal from '../../AlertModal';
import { PageTitle } from '../../PageTitle';
import { FormBox } from '../../formBox';
import { FormHeading } from '../../formHeading';
import { getFormattedLabel } from '../../../utils/FormatFunctions';
import { MaskedInputForm } from '../../inputs/forms/MaskedInputForm';

interface ResponsibleFormProps {
  defaultValues?: any;
}

interface FormInputsProps {
  name: string;
  value: any;
  placeholder?: string;
  label: string;
  yup: any;
}

export default function ResponsibleForm({ defaultValues = {} }: ResponsibleFormProps) {
  const validationToast = useFormValidationToast();
  const baseUrl = '/v1/admin/users';
  const { requestApi: reqApiTeachers, error: errorTeachers } = useFetch(baseUrl, {
    actionFeedback: false,
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { itemId } = useParams();
  const navigate = useNavigate();
  const [images, setImages] = useState<any>('');
  const [uploading, setUploading] = useState<any>(false);

  const optionsGenre = [
    {
      label: 'Masculino',
      value: 'M',
    },
    {
      label: 'Feminino',
      value: 'F',
    },
  ];

  const yupschema: any = {};
  const defaultValuesSchema: any = {};

  const formInputs: FormInputsProps[] = [
    {
      name: 'name',
      value: defaultValues.name ?? '',
      placeholder: 'Nome do usuário',
      label: 'Nome',
      yup: Yup.string()
        .required('Este campo é obrigatório!')
        .min(3, 'Nome deve ter pelo menos 3 caracteres'),
    },
    {
      name: 'email',
      value: defaultValues.email ?? '',
      placeholder: 'usuario@email.com',
      label: 'Email',
      yup: Yup.string().email('Email inválido').required('Este campo é obrigatório!'),
    },
    {
      name: 'cell_phone',
      value: defaultValues?.cell_phone ?? '',
      placeholder: 'Telefone do responsável',
      label: 'Telefone',
      yup: Yup.string().required('Este campo é obrigatório!'),
    },
    {
      name: 'genre',
      value: defaultValues?.genre
        ? optionsGenre.find((item) => item.value === defaultValues?.genre)
        : null,
      placeholder: 'Gênero do usuário',
      label: 'Gênero',
      yup: Yup.mixed(),
    },
    {
      name: 'institutions',
      value: [...(defaultValues?.institutions?.map((item) => getFormattedLabel(item)) ?? [])],
      placeholder: 'Instituições do usuário',
      label: 'Instituições',
      yup: Yup.mixed().required('Este campo é obrigatório!'),
    },
    {
      name: 'password',
      value: '',
      placeholder: 'Senha',
      label: 'Senha',
      yup: defaultValues.name ? '' : Yup.string(),
    },
    {
      name: 'confirm_password',
      value: '',
      placeholder: 'Digite a senha novamente',
      label: 'Confirmação de senha',
      yup: defaultValues.name
        ? ''
        : Yup.string().oneOf([Yup.ref('password'), null], 'Senhas não iguais'),
    },
    {
      name: 'image',
      value: defaultValues.image?.original ?? null,
      placeholder: '',
      label: 'Foto de perfil',
      yup: Yup.mixed().nullable(),
    },
    {
      name: 'status',
      value: defaultValues.status ?? true,
      label: 'Status',
      yup: Yup.boolean(),
    },
  ];

  formInputs.forEach((object) => {
    yupschema[object.name] = object.yup;
    defaultValuesSchema[object.name] = object.value;
  });
  const schema = Yup.object().shape(yupschema);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: defaultValuesSchema,
  });

  const name = formInputs.find((field) => field.name === 'name');
  const email = formInputs.find((field) => field.name === 'email');
  const cellPhone = formInputs.find((field) => field.name === 'cell_phone');
  const genre = formInputs.find((field) => field.name === 'genre');
  const image = formInputs.find((field) => field.name === 'image');
  const status = formInputs.find((field) => field.name === 'status');

  const onSubmit = async (values: any) => {
    const params = {
      ...values,
      group_id: 5,
      genre: values.genre?.value ?? null,
      institutions: values.institutions.map((item) => ({ id: item.value })),
      password: defaultValues.name ? _ : values.password,
      confirm_password: _,
      image: _,
    };
    const { status: resStatus, data } = await reqApiTeachers(
      defaultValues.name ? 'put' : 'post',
      params,
      defaultValues.name ? `${baseUrl}/${itemId}` : null
    );
    if ((resStatus === 200 || resStatus === 201) && images) {
      setUploading(true);
      const apiUrlId = itemId ?? data?.data?.id;
      await reqApiTeachers('put', { file: images.toString() }, `${baseUrl}/${apiUrlId}/images`);
      setUploading(false);
    }
    onOpen();
  };

  useEffect(() => {
    if (defaultValues.name) {
      reset(defaultValuesSchema);
    }
  }, [defaultValues]);

  return (
    <FormBox>
      <Flex>
        <ReturnButton previousPage="responsible" mb={5} />
        <PageTitle>{defaultValues.name ? 'Editar' : 'Criar'} responsável</PageTitle>
      </Flex>

      <AlertModal
        alertMsg={
          <Text textAlign="center" fontWeight="bold" fontSize="25px">
            {!errorTeachers
              ? `Responsável ${defaultValues.name ? 'editado' : 'criado'} com sucesso!`
              : errorTeachers?.msg}
          </Text>
        }
        msgStatus={errorTeachers ? 'error' : 'success'}
        closeOnOverlayClick={!!errorTeachers}
        useSecondaryActions
        isOpenSecondary={isOpen}
        onCloseSecondary={onClose}
        ConfirmButton={
          <Button
            w="250px"
            boxShadow="0px 5px 10px #00000065"
            borderRadius="full"
            onClick={() => navigate('/responsible')}
          >
            Retornar para a listagem
          </Button>
        }
      />

      <form noValidate onSubmit={handleSubmit(onSubmit, validationToast)}>
        <FormHeading>Informações Básicas</FormHeading>
        <Flex gap={5}>
          <Box flex={1}>
            <InputForm
              name={name?.name}
              label={name?.label}
              placeholder={name?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </Box>
          <Box flex={1}>
            <InputForm
              name={email?.name}
              label={email?.label}
              placeholder={email?.placeholder}
              isRequired
              register={register}
              errors={errors}
            />
          </Box>
          <Box flex={1}>
            <MaskedInputForm
              name={cellPhone?.name}
              label={cellPhone?.label}
              placeholder={cellPhone?.placeholder}
              isRequired
              control={control}
              errors={errors}
              mask="(99) 99999-9999"
            />
          </Box>
        </Flex>

        <Flex gap={5}>
          <Box flex={2}>
            <SelectInputForm
              name={genre?.name}
              label={genre?.label}
              options={optionsGenre}
              placeholder={genre?.placeholder}
              control={control}
              errors={errors}
            />
          </Box>

          <Box flex={1}>
            <SwitchInputForm
              name={status?.name}
              label={status?.label}
              errors={errors}
              control={control}
            />
          </Box>
        </Flex>

        {/* 
        {!defaultValues?.name && (
          <>
            <FormHeading>Segurança</FormHeading>
            <Flex gap={5}>
              <Box flex={1}>
                <PasswordInputForm
                  name={password?.name}
                  label={password?.label}
                  placeholder={password?.placeholder}
                  register={register}
                  errors={errors}
                />
              </Box>

              <Box flex={1}>
                <PasswordInputForm
                  name={confirmPassword?.name}
                  label={confirmPassword?.label}
                  placeholder={confirmPassword?.placeholder}
                  register={register}
                  errors={errors}
                />
              </Box>
            </Flex>
          </>
        )} */}

        <Box mt={5}>
          <ImageInputForm
            defaultVal={defaultValues?.image}
            setImages={setImages}
            setIsUpping={setUploading}
            name={image?.name}
            label={image?.label}
            register={register}
            watch={watch}
            errors={errors}
          />
        </Box>

        <Flex mr="auto">
          <FormButton
            mt={4}
            w="100px"
            isLoading={isSubmitting || uploading}
            type="submit"
            textTransform="uppercase"
          >
            Salvar
          </FormButton>
        </Flex>
      </form>
    </FormBox>
  );
}
